// src/utils/reportPdf.js — the rules a defect-report PDF is built by, in ONE
// place, for every screen that builds one.
// ---------------------------------------------------------------------------
// v0.218.0. Brett: "what we produce is what they get. The html version is
// preference as its clickable however sometimes a pdf is needed."
//
// Until now only ReportScreen built the PDF, and its defaults, its row shapes
// and its brand rule lived inside that screen. The shared report now builds
// the same PDF for a recipient, and two copies of those rules would drift: the
// recipient's PDF would quietly stop being the one the sender made. So they
// live here, and both screens use them.

import { DEFAULT_BRAND } from './pdf-shell.js';
import { NATIVE_API_BASE } from './nativeShell.js';
import { pairFirst, pairRoles } from './photoPairs.js';

/**
 * The web address printed on a report's cover: the one the report was made on.
 * In the phone app the page is served from localhost (or capacitor://), which
 * is nobody's address, so the app's public one is used instead.
 */
export function reportSiteHost(loc = (typeof window !== 'undefined' ? window.location : null)) {
  try {
    if (loc && /^https?:$/.test(loc.protocol) && !/^(localhost|127\.|\[::1\]|0\.0\.0\.0)/.test(loc.hostname)) return loc.host;
  } catch (_e) { /* fall through */ }
  return new URL(NATIVE_API_BASE).host;
}

// Moved from ReportScreen.jsx (v0.218.0), unchanged.
// v0.37.0 — clientName retired from report settings. Customer name now
// lives on the Project (Projects.CustomerName) and the report header
// reads it from project.CustomerName. clientName stays readable in
// existing SettingsJson rows but is no longer surfaced as an input.
// preparedBy is the new per-report field: the operator who built the
// PDF. Autofilled from getCurrentUser(), editable per report.
export const DEFAULT_SETTINGS = {
  paperSize: 'a4',
  title: 'Defect Report',
  preparedBy: '',
  // v0.41.0 — Three report formats. Saved in settingsJson on the
  //           bound Report row so each saved report remembers which
  //           layout it used.
  format: 'snag-list',
  // v0.45.30 — Cover-page visual theme. Independent of format.
  //   'cinematic' — dark navy gradient + orange accent (the original look)
  //   'light'     — white cover, navy headlines, brand-orange accent line
  //   'mono'      — black + white minimal, single accent line, data-first
  // White-label tenants' brand colour still overrides the theme accent
  // when set in Tenant Settings.
  theme: 'cinematic',
  // v0.103.0 — Report layout style + accent colour. 'signature' is the
  //            pre-style output so nothing changes until the user picks.
  //            brandColor null = keep the tenant / DropPin default.
  styleKey: 'signature',
  brandColor: null,
  includeCover: true,
  includePhotos: true,
  includePlanThumbs: true,
  includeSignoff: false,
  filters: {
    status: [],   // [] = all
    priority: [],
    trade: [],
    drawing: [],
  },
};

export function normaliseDefect(row) {
  if (!row) return row;
  return {
    id: row.id ?? row.Id,
    projectId: row.projectId ?? row.ProjectId,
    drawingId: row.drawingId ?? row.DrawingId ?? null,
    defectNumber: row.defectNumber ?? row.DefectNumber ?? null,
    description: row.description ?? row.Description ?? '',
    priority: row.priority ?? row.Priority ?? 'medium',
    status: row.status ?? row.Status ?? 'open',
    assignedTo: row.assignedTo ?? row.AssignedTo ?? row.assignee ?? null,
    locationLabel: row.locationLabel ?? row.LocationLabel ?? row.Location ?? null,
    // v0.9.0 — pull trade through (was missing in v0.8.0; default 'general').
    trade: row.trade ?? row.Trade ?? 'general',
    markX: row.markX ?? row.MarkX ?? null,
    markY: row.markY ?? row.MarkY ?? null,
    photoCount: row.photoCount ?? (Array.isArray(row.photos) ? row.photos.length : 0),
    createdAt: row.createdAt ?? row.CreatedAt ?? null,
    updatedAt: row.updatedAt ?? row.UpdatedAt ?? null,
    rectifiedAt: row.rectifiedAt ?? row.RectifiedAt ?? null,
    photos: pairedPhotos(Array.isArray(row.photos) ? row.photos.map(normalisePhoto) : []),
  };
}

// v0.317.0 - a fix photo and the photo it answers go first, each told its role.
function pairedPhotos(photos) {
  const roles = pairRoles(photos);
  if (!roles.size) return photos;
  return pairFirst(photos).ordered.map(p => (roles.has(String(p.id)) ? { ...p, pairRole: roles.get(String(p.id)) } : p));
}
export function normalisePhoto(p) {
  if (!p) return p;
  return {
    id: p.id ?? p.Id,
    url: p.url ?? p.Url,
    // v0.215.0 — kept: the report fetches the ~1600 px display copy first, not
    // the camera original (this mapping used to drop it).
    displayUrl: p.displayUrl ?? p.DisplayUrl ?? null,
    thumbnailUrl: p.thumbnailUrl ?? p.ThumbnailUrl,
    caption: p.caption ?? p.Caption,
    // v0.317.0 - a fix photo names the photo it answers.
    kind: p.kind ?? p.Kind ?? null,
    beforePhotoId: p.beforePhotoId ?? p.BeforePhotoId ?? null,
  };
}
export function normaliseDrawing(d) {
  if (!d) return d;
  // v0.9.0 — the v0.7.0 version dropped rasterUrl + sourceUrl on the floor
  // (only mapped a non-existent .fileDataUrl alias), so the template's plan
  // section always rendered "Drawing image not available". The API returns
  // both rasterUrl (PNG raster — best for the report) and sourceUrl (the
  // original PDF or PNG); pass both through so the template can pick.
  return {
    id: d.id ?? d.Id,
    name: d.name ?? d.Name ?? d.filename ?? d.Filename ?? '',
    drawingNumber: d.drawingNumber ?? d.DrawingNumber ?? d.number ?? null,
    title: d.title ?? d.Title ?? d.name ?? d.Name ?? d.filename ?? d.Filename ?? '',
    // v0.38.0 — original upload filename, used by the PDF plan-page heading.
    sourceFileName: d.sourceFileName ?? d.SourceFileName ?? d.fileName ?? d.FileName ?? null,
    discipline: d.discipline ?? d.Discipline ?? '',
    rasterUrl: d.rasterUrl ?? d.RasterUrl ?? null,
    sourceUrl: d.sourceUrl ?? d.SourceUrl ?? d.url ?? d.Url ?? null,
    // v0.215.0 — which page of a PDF plan to draw when it has no picture yet.
    pageNumber: d.pageNumber ?? d.PageNumber ?? 1,
    fileDataUrl: d.fileDataUrl ?? d.FileDataUrl ?? d.rasterUrl ?? d.sourceUrl ?? d.url ?? d.Url ?? null,
    // 2026-09-15 — the plan's size (so the PDF keeps its shape and measures the
    // markup) and the markup itself.
    width: d.width ?? d.Width ?? null,
    height: d.height ?? d.Height ?? null,
    markup: d.markup ?? null,
  };
}

/**
 * The brand a report is printed under. Pro uses the tenant's name, colour and
 * logo; Free always prints DropPin's (with its watermark, which the template
 * governs from settings.tier). The report's own accent colour, when one was
 * picked, wins over both. Moved from ReportScreen.generate, unchanged.
 */
export function reportBrand({ tier, tenant, settings }) {
  const brand = (tier === 'pro' && tenant)
    ? {
        ...DEFAULT_BRAND,
        brandName:   tenant.brandName   || DEFAULT_BRAND.brandName,
        brandColor:  tenant.brandColor  || DEFAULT_BRAND.brandColor,
        logoDataUrl: tenant.brandLogoUrl || null,
      }
    : { ...DEFAULT_BRAND };
  if (settings && settings.brandColor) brand.brandColor = settings.brandColor;
  brand.website = reportSiteHost();
  return brand;
}

/**
 * A saved Report row's settings over a base, exactly as ReportScreen applies
 * them when it opens ?reportId=N: the row's settingsJson wins, filters merge
 * with the defaults, the report's name is the title when none was set, and the
 * row's PreparedBy wins.
 */
export function settingsFromSavedReport(saved, base = DEFAULT_SETTINGS) {
  let s = { ...base };
  if (!saved) return s;
  if (saved.settingsJson) {
    try {
      const parsed = typeof saved.settingsJson === 'string' ? JSON.parse(saved.settingsJson) : saved.settingsJson;
      s = { ...s, ...parsed, filters: { ...DEFAULT_SETTINGS.filters, ...((parsed && parsed.filters) || {}) } };
    } catch (_e) { /* keep the base */ }
  }
  if (saved.name && (!s.title || s.title === DEFAULT_SETTINGS.title)) s = { ...s, title: saved.name };
  if (saved.preparedBy) s = { ...s, preparedBy: saved.preparedBy };
  return s;
}

/** The keys a report's settings may carry, and nothing else — what a share stores. */
export const RENDER_SETTING_KEYS = Object.keys(DEFAULT_SETTINGS);
export function renderSettingsOf(settings) {
  const out = {};
  for (const k of RENDER_SETTING_KEYS) if (settings && settings[k] !== undefined) out[k] = settings[k];
  return out;
}
